import { useState, useMemo, useCallback, useEffect } from "react";
import { Menubar } from "primereact/menubar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { MultiSelect } from "primereact/multiselect";
import { DataScroller } from "primereact/datascroller";
import { Tag } from "primereact/tag";
import { Slider } from "primereact/slider";
import { Dropdown } from "primereact/dropdown";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { useNavigate } from "react-router-dom";
import Loader from "./loader";
import styles from "./FabricList.module.scss";
import GoogleSheetService from "../services/GoogleSheetService.js";

// Función para parsear composición en partes { fiber, percent }
const parseComposition = (composition = "") => {
  const regex = /([A-Za-zÁÉÍÓÚáéíóúÑñ ]+?)\s*(\d+)%/g;
  const parts = [];
  let match;
  while ((match = regex.exec(composition))) {
    const fiber = match[1].trim();
    const percent = Number(match[2]);
    if (fiber) parts.push({ fiber, percent });
  }
  if (parts.length === 0 && composition) {
    parts.push({ fiber: composition.trim(), percent: null });
  }
  return parts;
};

const FabricList = () => {
  const navigate = useNavigate();

  // Estados para filtros
  const [globalFilter, setGlobalFilter] = useState("");
  const [filterSidebar, setFilterSidebar] = useState(false);
  const [selectedCompositions, setSelectedCompositions] = useState([]);
  const [selectedRestrictions, setSelectedRestrictions] = useState([]);
  const [weightRange, setWeightRange] = useState([0, 400]);
  const [widthRange, setWidthRange] = useState([0, 3]);
  const [sortBy, setSortBy] = useState("name-asc");

  // Datos
  const [rawFabrics, setRawFabrics] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carga de datos inicial
  useEffect(() => {
    const loadFabricData = async () => {
      try {
        setLoading(true);
        const data = await GoogleSheetService.getFabrics();
        setRawFabrics(data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("No se pudo cargar las telas");
      } finally {
        setLoading(false);
      }
    };
    loadFabricData();
  }, []);

  // Preprocesamiento: parsear composiciones, normalizar
  useEffect(() => {
    const processed = rawFabrics.map((fabric) => ({
      ...fabric,
      compositionParts: parseComposition(fabric.composition),
      weight: Number(fabric.weight) || 0,
      width: Number(fabric.width) || 0,
    }));
    setFabrics(processed);
  }, [rawFabrics]);

  // Opciones de filtros
  const compositionOptions = useMemo(() => {
    const setFibers = new Set();
    fabrics.forEach(({ compositionParts }) =>
      compositionParts.forEach((p) => setFibers.add(p.fiber))
    );
    return Array.from(setFibers)
      .sort((a, b) => a.localeCompare(b))
      .map((fiber) => ({ label: fiber, value: fiber }));
  }, [fabrics]);

  const restrictionOptions = [
    { label: "Sin restricción", value: "Sin restricción" },
    { label: "Bajo Programación", value: "Bajo Programación" },
    { label: "Restricción", value: "Restricción" },
  ];

  const sortOptions = [
    { label: "Nombre A-Z", value: "name-asc" },
    { label: "Nombre Z-A", value: "name-desc" },
    { label: "Código A-Z", value: "code-asc" },
    { label: "Código Z-A", value: "code-desc" },
    { label: "Peso menor", value: "weight-asc" },
    { label: "Peso mayor", value: "weight-desc" },
    { label: "Precio menor", value: "price-asc" },
    { label: "Precio mayor", value: "price-desc" },
  ];

  // Filtrado y ordenamiento más precisos
  const filteredAndSortedFabrics = useMemo(() => {
    let list = fabrics;

    // Filtro global
    if (globalFilter) {
      const term = globalFilter.toLowerCase();
      list = list.filter(
        (f) =>
          f.code.toLowerCase().includes(term) ||
          f.name.toLowerCase().includes(term) ||
          f.composition.toLowerCase().includes(term)
      );
    }

    // Filtro por composición: debe contener todas las fibras seleccionadas
    if (selectedCompositions.length > 0) {
      list = list.filter((f) =>
        selectedCompositions.every((sel) =>
          f.compositionParts.some((p) => p.fiber === sel)
        )
      );
    }

    // Filtro por restricción
    if (selectedRestrictions.length > 0) {
      list = list.filter((f) => selectedRestrictions.includes(f.restriction));
    }

    // Peso y ancho
    list = list.filter(
      (f) =>
        f.weight >= weightRange[0] &&
        f.weight <= weightRange[1] &&
        f.width >= widthRange[0] &&
        f.width <= widthRange[1]
    );

    // Ordenamiento
    const [field, dir] = sortBy.split("-");
    list.sort((a, b) => {
      let av = a[field];
      let bv = b[field];
      if (field === "price") {
        av = Object.values(a.prices)[0]?.blancoKg || 0;
        bv = Object.values(b.prices)[0]?.blancoKg || 0;
      }
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [
    fabrics,
    globalFilter,
    selectedCompositions,
    selectedRestrictions,
    weightRange,
    widthRange,
    sortBy,
  ]);

  const getRestrictionSeverity = useCallback((restriction) => {
    switch (restriction) {
      case "Sin restricción":
        return "success";
      case "Bajo Programación":
        return "warning";
      case "Restricción":
        return "danger";
      default:
        return "info";
    }
  }, []);

  const clearAllFilters = useCallback(() => {
    setGlobalFilter("");
    setSelectedCompositions([]);
    setSelectedRestrictions([]);
    setWeightRange([0, 500]);
    setWidthRange([0, 3]);
    setSortBy("name-asc");
  }, []);

  const handleFabricClick = (fabric) => {
    localStorage.setItem("selectedFabric", JSON.stringify(fabric));
    navigate(`/tela/${fabric.code}`);
  };

  const itemTemplate = useCallback(
    (fabric) => (
      <div className={styles.fabricCard}>
        <Card className={styles.card} onClick={() => handleFabricClick(fabric)}>
          <div className={styles.cardHeader}>
            <div className={styles.fabricInfo}>
              <h3 className={styles.fabricName}>
                {fabric.name === "#N/A" ? "No definido" : fabric.name}
              </h3>
              <p className={styles.fabricCode}>
                {fabric.code || "No Definido"}
              </p>
              <Tag
                value={fabric.restriction || "No definido"}
                severity={getRestrictionSeverity(fabric.restriction)}
                className={styles.restrictionTag}
              />
            </div>
          </div>
          <Divider className={styles.divider} />
          <div className={styles.cardBody}>
            <div className={styles.composition}>
              <i className="pi pi-tags" />
              <span>{fabric.composition || "No definido"}</span>
            </div>
          </div>
        </Card>
      </div>
    ),
    [getRestrictionSeverity, navigate]
  );

  // Toolbar
  const start = (
    <div className={styles.toolbarStart}>
      <i className="pi pi-search" />
      <span className="p-input-icon-left">
        <InputText
          placeholder="Buscar telas..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className={styles.searchInput}
        />
      </span>
    </div>
  );
  const end = (
    <div className={styles.toolbarEnd}>
      <Button
        icon="pi pi-sort-amount-down"
        className={styles.sortButton}
        text
        onClick={() => setFilterSidebar(true)}
      />
    </div>
  );

  if (loading) return <Loader />;
  if (error) return <div>{error}</div>;

  return (
    <>
      <div className={styles.fabricList}>
        <Menubar
          model={[]}
          start={start}
          end={end}
          className={styles.menubar}
        />
        <div className={styles.content}>
          <div className={styles.resultsHeader}>
            <div className={styles.resultsInfo}>
              <span className={styles.resultsCount}>
                {filteredAndSortedFabrics.length} tela
                {filteredAndSortedFabrics.length !== 1 ? "s" : ""}
              </span>
            </div>
            <Dropdown
              value={sortBy}
              options={sortOptions}
              onChange={(e) => setSortBy(e.value)}
              className={styles.sortDropdown}
              placeholder="Ordenar por"
            />
          </div>
          <DataScroller
            value={filteredAndSortedFabrics}
            itemTemplate={itemTemplate}
            rows={10}
            inline
            scrollHeight="calc(100vh - 190px)"
            className={styles.dataScroller}
            emptyMessage={
              <div className={styles.emptyMessage}>
                <i className="pi pi-search" />
                <p>No se encontraron telas con los filtros aplicados</p>
                <Button
                  label="Limpiar filtros"
                  icon="pi pi-filter-slash"
                  onClick={clearAllFilters}
                  text
                />
              </div>
            }
          />
        </div>
        <Sidebar
          visible={filterSidebar}
          onHide={() => setFilterSidebar(false)}
          position="right"
          className={styles.filterSidebar}
          header={
            <div className={styles.sidebarHeader}>
              <span>Filtros Avanzados</span>
              <Button
                icon="pi pi-filter-slash"
                label="Limpiar"
                onClick={clearAllFilters}
                text
                size="small"
              />
            </div>
          }
        >
          <div className={styles.filterContent}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <i className="pi pi-tags" />
                Composición
              </label>
              <MultiSelect
                value={selectedCompositions}
                options={compositionOptions}
                onChange={(e) => setSelectedCompositions(e.value)}
                placeholder="Seleccionar fibras"
                display="chip"
                className={styles.multiSelect}
                maxSelectedLabels={2}
                filter
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <i className="pi pi-exclamation-triangle" />
                Restricción
              </label>
              <MultiSelect
                value={selectedRestrictions}
                options={restrictionOptions}
                onChange={(e) => setSelectedRestrictions(e.value)}
                placeholder="Seleccionar restricciones"
                display="chip"
                className={styles.multiSelect}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <i className="pi pi-chart-bar" />
                Peso (g/m²): {weightRange[0]} - {weightRange[1]}
              </label>
              <Slider
                value={weightRange}
                onChange={(e) => setWeightRange(e.value)}
                range
                min={0}
                max={400}
                step={10}
                className={styles.slider}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <i className="pi pi-arrows-h" />
                Ancho (m): {widthRange[0]} - {widthRange[1]}
              </label>
              <Slider
                value={widthRange}
                onChange={(e) => setWidthRange(e.value)}
                range
                min={0}
                max={3}
                step={0.1}
                className={styles.slider}
              />
            </div>
          </div>
        </Sidebar>
      </div>
    </>
  );
};

export default FabricList;
